import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { finalize, tap } from 'rxjs/operators';
import { environment } from '../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class HnsScreeningService {
  private readonly baseUrl = `${environment.baseUrl}/Hns`;
  private readonly http = inject(HttpClient);
  private readonly screeningSignal = signal<HnsResponse | null>(null);
  private loading = false;
  readonly screening = this.screeningSignal.asReadonly();

  resetScreening(): void {
    this.screeningSignal.set(null);
    this.loading = false;
  }

  loadScreening(): void {
    if (!this.loading && !this.screeningSignal()) {
      this.loading = true;
      this.http
        .get<HnsResponse>(`${this.baseUrl}`)
        .pipe(finalize(() => (this.loading = false)))
        .subscribe({
          next: response => this.screeningSignal.set(response),
          error: () => {
            this.loading = false;
          },
        });
    }
  }

  refreshScreening() {
    return this.http.get<HnsResponse>(`${this.baseUrl}`).pipe(
      tap(response => this.screeningSignal.set(response)),
    );
  }

  updateQuestionResponse(questionId: number, answerId: number) {
    const body: HnsQuestionResponse = {
      questionId,
      selectedAnswers: [
        {
          answerId,
          supplementaryData: null,
        },
      ],
    };
    return this.http
      .put(`${this.baseUrl}/Question/${questionId}`, body)
      .pipe(tap(() => this.updateLocalResponse(questionId, body.selectedAnswers ?? [])));
  }

  updateQuestionResponses(questionId: number, answerIds: number[]) {
    const body: HnsQuestionResponse = {
      questionId,
      selectedAnswers: answerIds.map(answerId => ({
        answerId,
        supplementaryData: null,
      })),
    };
    return this.http
      .put(`${this.baseUrl}/Question/${questionId}`, body)
      .pipe(tap(() => this.updateLocalResponse(questionId, body.selectedAnswers ?? [])));
  }

  updateQuestionResponseWithSupplementaryData(
    questionId: number,
    answerId: number,
    supplementaryData: string | null,
  ) {
    const body: HnsQuestionResponse = {
      questionId,
      selectedAnswers: [
        {
          answerId,
          supplementaryData,
        },
      ],
    };
    return this.http
      .put(`${this.baseUrl}/Question/${questionId}`, body)
      .pipe(tap(() => this.updateLocalResponse(questionId, body.selectedAnswers ?? [])));
  }

  cleanupFollowUpAnswers() {
    return this.http.post(`${this.baseUrl}/CleanupFollowUpAnswers`, null);
  }

  deleteQuestionResponse(questionId: number) {
    return this.http
      .delete(`${this.baseUrl}/Question/${questionId}`)
      .pipe(tap(() => this.updateLocalResponse(questionId, [])));
  }

  completeSurvey(language: string) {
    return this.http.post(`${this.baseUrl}/Complete/${language}`, null);
  }

  completeChat() {
    return this.http.post(`${this.baseUrl}/CompleteChat`, null);
  }

  updateQuestionResponsesWithSupplementaryData(
    questionId: number,
    answerIds: number[],
    supplementaryData: string | null,
  ) {
    const body: HnsQuestionResponse = {
      questionId,
      selectedAnswers: answerIds.map((id, i) => ({
        answerId: id,
        supplementaryData: i === 0 ? supplementaryData : null,
      })),
    };
    return this.http
      .put(`${this.baseUrl}/Question/${questionId}`, body)
      .pipe(tap(() => this.updateLocalResponse(questionId, body.selectedAnswers ?? [])));
  }

  private updateLocalResponse(questionId: number, selectedAnswers: HnsAnswerResponse[]) {
    const current = this.screeningSignal();
    if (!current) {
      return;
    }

    const existing = current.questionResponses ?? [];
    const index = existing.findIndex(question => question.questionId === questionId);
    let updatedResponses: HnsQuestionResponse[];

    if (index >= 0) {
      updatedResponses = [...existing];
      updatedResponses[index] = { questionId, selectedAnswers };
    } else {
      updatedResponses = [...existing, { questionId, selectedAnswers }];
    }

    this.screeningSignal.set({
      ...current,
      questionResponses: updatedResponses,
    });
  }
}

export interface HnsResponse {
  id: number;
  version: number;
  isChatEligible: boolean;
  isMinor: boolean;
  memberSex: string | null;
  questionResponses: HnsQuestionResponse[];
}

export interface HnsQuestionResponse {
  questionId: number | null; // C# int? -> number | null
  selectedAnswers: HnsAnswerResponse[] | null; // C# List<>? -> array or null
}

export interface HnsAnswerResponse {
  answerId: number | null; // C# int? -> number | null
  supplementaryData?: string | null; // C# string? -> string | null
}
